/**
 * 数据转换相关工具函数
 */

import { TreeData, FlatTreeData, FlatTreeKeysMap } from '../iTree';
import { convertListToTree } from './util';

interface FieldNames {
    key?: string;
    title?: string;
    children?: string;
}

interface ConvertListAttrs {
    id: string;
    parendId: string;
    name: string;
    rootId: null;
    isLeaf: string;
    children: string;
}

/**
 * 深度遍历 treeData，把Tree数据拍平，变为一维数组
 * @param {Object} options 配置选项
 * @param {TreeData[]} options.treeData 树形数据
 * @param {string | null} options.parentKey 标识父节点
 * @param {boolean} options.isShown 该节点是否显示在页面中，当节点的父节点是展开状态 或 该节点是根节点时，该值为 true
 * @param {Set<string>} options.expandedKeysSet 展开的节点集合
 * @param {FlatTreeKeysMap} options.flatTreeKeysMap 存储所有 key-value 的映射
 * @param {FieldNames} options.fieldNames 字段名配置
 * @param {boolean} options.loadData 是否有异步加载
 * @returns {FlatTreeData[]} 拍平后的数据
 */
export function deepTraversal(options: {
    treeData?: TreeData[];
    parentKey?: string | null;
    isShown?: boolean;
    expandedKeysSet?: Set<string>;
    flatTreeKeysMap?: FlatTreeKeysMap;
    fieldNames?: FieldNames;
    loadData?: boolean;
} = {}): FlatTreeData[] {
    const {
        treeData = [],
        parentKey = null,
        isShown,
        expandedKeysSet,
        flatTreeKeysMap,
        fieldNames,
        loadData
    } = options;

    let flatTreeData: FlatTreeData[] = [];
    const dataCopy = treeData;

    if (Array.isArray(dataCopy)) {
        for (let i = 0, l = dataCopy.length; i < l; i++) {
            let { key, title, children, ...props } = dataCopy[i];
            let dataCopyI: FlatTreeData = new Object();

            // 处理字段名映射
            title = fieldNames?.title ? dataCopy[i][fieldNames.title] : title;
            key = (fieldNames?.key ? dataCopy[i][fieldNames.key] : key) + "";
            children = fieldNames?.children ? dataCopy[i][fieldNames.children] : children;

            // 叶子节点判断：不存在子节点，排除动态加载时 children 为[] 仍被认为有子节点
            const isLeaf = !(children?.length || (children?.length === 0 && loadData));

            // 判断节点是否展开
            let isExpanded = false;
            if (expandedKeysSet) {
                isExpanded = (parentKey === null || expandedKeysSet.has(parentKey))
                    ? expandedKeysSet.has(key as string)
                    : false;
            }

            dataCopyI = Object.assign(dataCopyI, {
                key,
                title,
                isExpanded,
                parentKey: parentKey || null,
                isShown,
                isLeaf
            }, { ...props });

            // 该节点的父节点是展开状态 或 该节点是根节点
            if (isShown || parentKey === null) {
                flatTreeData.push(dataCopyI);
                if (flatTreeKeysMap) {
                    flatTreeKeysMap[key] = dataCopyI;
                }
            }

            if (Array.isArray(children) && children.length > 0 && isExpanded) {
                // 若存在children则递归调用，把数据拼接到新数组中
                flatTreeData = flatTreeData.concat(
                    deepTraversal({
                        treeData: children,
                        parentKey: key,
                        isShown: isExpanded,
                        expandedKeysSet,
                        flatTreeKeysMap,
                        fieldNames,
                        loadData
                    })
                );
            }
        }
    }
    return flatTreeData;
}

/**
 * 将扁平化的树数据转换回树形结构
 * @param {FlatTreeData[]} treeList 扁平化的树数据
 * @param {FlatTreeKeysMap} flatTreeKeysMap 键值映射
 * @param {FieldNames} fieldNames 字段名配置
 * @param {number} startIndex 开始索引
 * @param {number} endIndex 结束索引
 * @returns {{treeData: TreeData[], startIndex: number, endIndex: number}} 树形数据和索引
 */
export function handleTreeListChange(
    treeList: FlatTreeData[],
    flatTreeKeysMap: FlatTreeKeysMap,
    fieldNames?: FieldNames,
    startIndex?: number,
    endIndex?: number
): {
    treeData: TreeData[];
    startIndex: number;
    endIndex: number;
} {
    // 属性配置设置
    const attr: ConvertListAttrs = {
        id: 'key',
        parendId: 'parentKey',
        name: 'title',
        rootId: null,
        isLeaf: 'isLeaf',
        children: fieldNames?.children || 'children'
    };

    const treeData = convertListToTree(treeList, attr, flatTreeKeysMap);

    return {
        treeData,
        startIndex: typeof startIndex !== "undefined" ? startIndex : 0,
        endIndex: typeof endIndex !== "undefined" ? endIndex : treeList.length
    };
}